import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { assignState } from '@/store/index.js';
import useAuthStore from '@/store/modules/auth.js';
import useSocketHook from '@/hooks/useSocket.js';
import {
  apiClearAllNotifications,
  apiMarkAllNotificationAsRead,
  apiMarkNotificationAsRead,
  apiRetrieveNotifications,
} from '@/services/notification.js';

const useNotificationStore = defineStore('notification', () => {
  const {
    createStompClient,
    connectStompClient,
    removeStompClient,
    subscribeToChannel,
    unsubscribeFromChannel,
  } = useSocketHook(
    '/notificationsData',
    { notificationChannel: null },
  );

  const notificationList = ref([]);

  const hasUnreadNotifications = computed(() => notificationList.value.some(({ readStatus }) => !readStatus));

  const dispatchRetrieveNotifications = async () => {
    try {
      const { parsedData } = await apiRetrieveNotifications();

      assignState(notificationList, parsedData);
    } catch {
      assignState(notificationList, []);
    }
  };

  const dispatchMarkNotificationAsRead = async (notificationId) => {
    try {
      const notificationIndex = notificationList.value.findIndex(({ id }) => id === notificationId);

      if (notificationIndex === -1) { return; }

      notificationList.value[notificationIndex].readStatus = true;

      await apiMarkNotificationAsRead({ queryParams: { notificationId } });
    } catch (error) {
      return Promise.resolve(error);
    }
  };

  const dispatchMarkAllNotificationAsRead = async () => {
    try {
      notificationList.value.forEach((notification) => {
        notification.readStatus = true;
      });

      await apiMarkAllNotificationAsRead();
    } catch (error) {
      return Promise.resolve(error);
    }
  };

  const dispatchClearAllNotifications = async () => {
    try {
      await apiClearAllNotifications();

      notificationList.value = [];
    } catch (error) {
      return Promise.resolve(error);
    }
  };

  const connectNotificationStompClient = () => {
    const authStore = useAuthStore();

    connectStompClient(() => {
      subscribeToChannel(
        'notificationChannel',
        `/topic/notificationsData/${ authStore.user.msisdn }`,
        (parsedData) => {
          console.log({ parsedData });
          notificationList.value.unshift(parsedData);
        },
      );
    });
  };

  const clearNotificationSocket = async () => {
    unsubscribeFromChannel('notificationChannel');

    await removeStompClient();
  };

  return {
    notificationList,
    hasUnreadNotifications,
    dispatchRetrieveNotifications,
    dispatchMarkNotificationAsRead,
    dispatchMarkAllNotificationAsRead,
    dispatchClearAllNotifications,
    createNotificationStompClient: createStompClient,
    connectNotificationStompClient,
    clearNotificationSocket,
  };
});

export default useNotificationStore;
